// @flow

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import * as React from 'react'

import { colors, media, variables } from '@toggl/style'

import * as Button from '../button'
import * as text from '../text'

import Links from './links'

export { default as ExpandedMenu } from './expandedMenu'
export { default as MenuContent } from './menuContent'
export { Product, ProductsGroup } from './product'
export * from './backSocial'

type Props = {
  Cta: React.ComponentType<any>,
  background?: string,
  children: React.Node,
  login: React.Node,
  logo: React.Element<any>,
  logoWidth: [string, string],
  onToggle: (open: boolean) => void,
  open: boolean,
}

export const Header = ({
  Cta,
  background,
  children,
  login,
  logo,
  logoWidth,
  onToggle,
  open,
}: Props) => {
  const [mobileOpen, setMobileOpen] = React.useState(false)

  return (
    <HeaderLine background={background} open={open}>
      <LogoWrapper logoWidth={logoWidth}>{logo}</LogoWrapper>

      <DesktopLinks open={open}>
        {children}
        <Gap />
        {login}
        <Cta />
      </DesktopLinks>

      <MobileLinksWrapper open={mobileOpen}>
        <Links>{children}</Links>

        <CtaWrapper>
          <CtaButtonWrapper>
            <Cta />
          </CtaButtonWrapper>
          <div>{login}</div>
        </CtaWrapper>
      </MobileLinksWrapper>
      <Burger
        open={mobileOpen}
        onClick={() => {
          onToggle(!mobileOpen)
          setMobileOpen(!mobileOpen)
        }}
      >
        <div />
        <div />
        <div />
      </Burger>
    </HeaderLine>
  )
}

const Gap = styled.div`
  flex: 1 auto;
`

const DesktopLinks = styled.nav`
  display: none;
  width: 100%;
  align-items: center;
  justify-content: space-between;

  > *:not(:last-child) {
    margin-right: 12px;
  }

  > ${Gap} {
    margin-right: 0;
  }

  a {
    text-decoration: none;
  }

  ${Button.Secondary}, ${Button.HeaderButtonWrapper}, a {
    color: ${(p) => (p.open ? colors.darkPurple : colors.almostWhite)};
  }

  ${media.mq[1]} {
    ${text.heading5};
    display: flex;
  }

  ${media.mq[2]} {
    > *:not(:last-child) {
      margin-right: 3%;
    }
  }
`

const MobileLinksWrapper = styled.div`
  ${text.heading4};
  background: ${(p) => p.theme.background};
  flex-direction: column;
  margin: 0 -20px;
  padding: 30px 20px;
  position: absolute;
  top: 100%;
  height: calc(100vh - 100%);
  transition: all 300ms linear;
  width: 100%;
  overflow: hidden;

  display: ${(p) => (p.open ? 'flex' : 'none')};

  ul > li {
    margin-bottom: 25px;

    a,
    button,
    button:hover {
      text-decoration: none;
      color: ${colors.fadedPurple};
    }
  }

  ${media.mq[1]} {
    display: none;
  }
`

const CtaButtonWrapper = styled.div`
  margin-bottom: 20px;

  ${media.mq[1]} {
    margin: 0;
  }
`

const LogoWrapper = styled.div`
  width: ${(p) => p.logoWidth[0]};
  display: flex;
  flex-shrink: 0;
  margin-right: 30px;

  ${media.mq[2]} {
    width: ${(p) => p.logoWidth[1]};
  }

  a {
    display: flex;
  }

  svg {
    width: 100%;
  }
`

const openAnimateCss = css`
  transition: background-color 100ms ease;
  transition-delay: 200ms;
`

const HeaderLine: React.ComponentType<any> = styled.div`
  align-items: center;
  display: flex;
  background-color: ${(p) => p.theme.background};
  border-bottom: solid 1px ${(p) => p.theme.borderColor};
  height: ${variables.headerHeight}px;
  justify-content: space-between;
  padding: 0 20px;

  ${(p) => (!p.open ? openAnimateCss : '')}

  ${media.mq[1]} {
    height: ${variables.headerHeightFull}px;
    border-bottom: none;
    background-color: ${({ open, theme, background }) =>
      open ? theme.backgroundDesktop : background || 'transparent'};
  }
`

const Burger: React.ComponentType<any> = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 24px;
  height: 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;

  &:focus {
    outline: none;
  }

  ${media.mq[1]} {
    display: none;
  }

  div {
    width: 24px;
    height: 2px;
    background: ${colors.darkPurple};
    border-radius: 10px;
    position: relative;
    transform-origin: 3px;

    :first-of-type {
      transform: ${({ open }) => (open ? 'rotate(45deg)' : 'rotate(0)')};
    }

    :nth-of-type(2) {
      opacity: ${({ open }) => (open ? '0' : '1')};
      transform: ${({ open }) => (open ? 'translateX(20px)' : 'translateX(0)')};
    }

    :nth-of-type(3) {
      transform: ${({ open }) => (open ? 'rotate(-45deg)' : 'rotate(0)')};
    }
  }
`

const CtaWrapper: React.ComponentType<any> = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: auto;

  ${media.mq[1]} {
    flex-direction: row;
    justify-content: space-between;
    margin-top: 0;
    margin-left: auto;
  }
`
